import {
  getWorkflowRuleGroups,
  toWorkflowRuleGroupsPayload,
  workflowRuleGroupsSchema,
  canPreviewWorkflowRuleGroups,
} from "../workflow-rule-groups.ts";
import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import { ControlledPerformanceReviewInvitationFields } from "@/pages/talent/performance-reviews/edit/components/PerformanceReviewInvitationFields.tsx";
import {
  copyCommonInvitationToEmptyReviewerTypes,
  createPerformanceReviewInvitationSchemaFields,
} from "@/pages/talent/performance-reviews/edit/performance-review-edit.ts";
import { getWorkflowPlaceholderOptions } from "@/components/rich-editor/rich-editor.types.ts";
import { TextareaWithVariableInsert } from "@/components/textarea-with-variable-insert/TextareaWithVariableInsert.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { WorkflowReminderSettingsSection } from "@/pages/workflows/edit/actions/WorkflowReminderSettingsSection.tsx";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import { useWorkflowUpdateBlockActionPerformanceReview } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiPerformanceReviewInvitationMode,
  ApiPerformanceReviewReviewerTypes,
} from "@/types/performance-review.types.ts";
import { ApiNotificationChannelType } from "@/types/notification.types.ts";
import { ReminderMode, ReminderPeriod, ReminderSettings } from "@/types/reminder.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import {
  ApiWorkflowBlockActionPerformanceReviewSave,
  ApiWorkflowSurveyPeriodType,
  getWorkflowSurveyPeriodItems,
} from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiWorkflowBlockActionTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Input,
  Modal,
  Radio,
  Select,
  Stack,
  Switch,
  TabItem,
  Tabs,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { WorkflowActionTimingEditor } from "@/pages/workflows/edit/actions/WorkflowActionTimingEditor.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
  useWorkflows,
} from "@/pages/workflows/workflow-hook.tsx";
import {
  getDefaultReviewerTypes,
  isAnonymousSupportedReviewerType,
  mapReviewerTypesToForm,
} from "./helpers";
import { WorkflowPerformanceReviewReviewerTypesSection } from "./WorkflowPerformanceReviewReviewerTypesSection";
import {
  WorkflowEditActionPerformanceReviewModalProps,
  WorkflowPerformanceReviewReviewerTypeFormData,
  WorkflowPerformanceReviewFormData,
} from "./types";

export const WorkflowEditActionPerformanceReviewModal = ({
  workflowVersion,
  block,
  actionPerformanceReview,
  ...props
}: WorkflowEditActionPerformanceReviewModalProps) => {
  const { t } = useTranslation();
  const participantConditionTypes = useRuleConditionTypes(
    RuleRegistryPlace.workflowPerformanceReviewParticipants
  );
  const { workflowActionTimingSchemaFields } = useWorkflows();
  const { close } = useModals();
  const { id } = { ...props };

  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const defaultReminderSettings = {
    channels: [
      ApiNotificationChannelType.in_app,
      ApiNotificationChannelType.email,
      ApiNotificationChannelType.slack,
    ],
    mode: ReminderMode.medium,
    custom_subject: null,
    custom_body: null,
    custom_frequency: null,
    custom_period: null,
  };

  const WorkflowEditActionPerformanceReviewSchemaObject = yup.object({
    title: yup.string().defined().nullable(),
    reminders_enabled: yup.boolean().required(),
    reminder_settings: yup.object({
      channels: yup
        .array()
        .of(
          yup
            .mixed<ApiNotificationChannelType>()
            .oneOf(Object.values(ApiNotificationChannelType))
            .required()
        )
        .default(defaultReminderSettings.channels),
      mode: yup
        .mixed<ReminderMode>()
        .oneOf(Object.values(ReminderMode))
        .required()
        .default(ReminderMode.medium),
      custom_subject: yup.string().nullable().default(null),
      custom_body: yup.string().nullable().default(null),
      custom_frequency: yup.number().nullable().default(null),
      custom_period: yup
        .mixed<ReminderPeriod>()
        .oneOf(Object.values(ReminderPeriod))
        .nullable()
        .default(null),
    }),
    ...workflowActionTimingSchemaFields,
    block_data: yup.object({
      title: yup.string().required(t("errors:field_required")),
      description: yup.string().defined().nullable(),
      emoji: yup.string().defined().nullable(),
      use_ai: yup.boolean().required(),
      participants_rule_groups: workflowRuleGroupsSchema(),
      ...createPerformanceReviewInvitationSchemaFields(t("errors:field_required")),
      invitation_mode: yup
        .mixed<ApiPerformanceReviewInvitationMode>()
        .oneOf(Object.values(ApiPerformanceReviewInvitationMode))
        .required(t("errors:field_required")),
      timeframe_period_type: yup
        .mixed<ApiWorkflowSurveyPeriodType>()
        .oneOf(Object.values(ApiWorkflowSurveyPeriodType))
        .required(t("errors:field_required")),
      timeframe_period_value: yup
        .number()
        .required(t("errors:field_required"))
        .typeError(t("errors:field_number_required")),
      reviewer_types_enabled_validation: yup
        .string()
        .defined()
        .nullable()
        .test("at-least-one-enabled", t("errors:reviewers_validation_error"), function () {
          return Boolean(
            this.parent?.reviewer_types?.some(
              (reviewerType: WorkflowPerformanceReviewReviewerTypeFormData) =>
                reviewerType.is_enabled
            )
          );
        }),
      reviewer_types: yup
        .array()
        .of(
          yup.object({
            reviewer_type: yup
              .mixed<ApiPerformanceReviewReviewerTypes>()
              .oneOf(Object.values(ApiPerformanceReviewReviewerTypes))
              .required(),
            id: yup.number().nullable().defined(),
            title: yup.string().defined().nullable(),
            form_template_id: yup
              .mixed<number>()
              .nullable()
              .defined()
              .test("required", t("errors:field_required"), function (value) {
                return this.parent?.is_enabled ? value != null : true;
              }),
            is_enabled: yup.boolean().required(),
            is_anonymous: yup.boolean().required(),
            anonymous_threshold: yup
              .number()
              .required(t("errors:field_required"))
              .typeError(t("errors:field_number_required")),
            participants_rule_groups: workflowRuleGroupsSchema(),
            ...createPerformanceReviewInvitationSchemaFields(
              t("errors:field_required"),
              (parent) => parent.is_enabled === true
            ),
          })
        )
        .min(1)
        .required(),
    }),
  });

  const {
    control,
    register,
    handleSubmit,
    getValues,
    trigger,
    setValue,
    formState: { errors },
  } = useForm<WorkflowPerformanceReviewFormData>({
    mode: "onChange",
    resolver: yupResolver(WorkflowEditActionPerformanceReviewSchemaObject),
    values: {
      title: block.title ?? "",
      ...getWorkflowActionTimingValues(block),
      reminders_enabled: !!actionPerformanceReview.reminder_settings?.mode,
      reminder_settings: actionPerformanceReview.reminder_settings?.mode
        ? (actionPerformanceReview.reminder_settings as ReminderSettings)
        : defaultReminderSettings,
      block_data: {
        title: actionPerformanceReview.title ?? "",
        description: actionPerformanceReview.description ?? "",
        emoji: actionPerformanceReview.emoji ?? emojiDefaults.performanceReview.default,
        use_ai: actionPerformanceReview.use_ai,
        participants_rule_groups: getWorkflowRuleGroups(
          actionPerformanceReview.participants_rule_groups,
          actionPerformanceReview.participants_rules
        ),
        invitation_subject: actionPerformanceReview.invitation_subject ?? "",
        invitation_body: actionPerformanceReview.invitation_body ?? "",
        invitation_mode:
          actionPerformanceReview.invitation_mode ?? ApiPerformanceReviewInvitationMode.common,
        timeframe_period_type: actionPerformanceReview.timeframe_period_type,
        timeframe_period_value: actionPerformanceReview.timeframe_period_value,
        reviewer_types_enabled_validation: "",
        reviewer_types: (() => {
          if (!actionPerformanceReview.reviewer_types.length) return getDefaultReviewerTypes();
          const mappedTypes = mapReviewerTypesToForm(actionPerformanceReview.reviewer_types);
          return mappedTypes.length ? mappedTypes : getDefaultReviewerTypes();
        })(),
      },
    },
  });

  const watchedParticipantGroups = useWatch({
    control,
    name: "block_data.participants_rule_groups",
  });
  const timeframePeriodValue = useWatch({ control, name: "block_data.timeframe_period_value" });
  const invitationMode = useWatch({ control, name: "block_data.invitation_mode" });
  const hasTimeframe = !!timeframePeriodValue && timeframePeriodValue > 0;
  const [useEmoji, setUseEmoji] = useState(!!actionPerformanceReview.emoji);
  const [activeTab, setActiveTab] = useState(0);

  const updateBlockAction = useWorkflowUpdateBlockActionPerformanceReview(control);

  const setInvitationMode = (mode: ApiPerformanceReviewInvitationMode) => {
    if (mode === ApiPerformanceReviewInvitationMode.by_reviewer_type) {
      setValue(
        "block_data.reviewer_types",
        copyCommonInvitationToEmptyReviewerTypes(getValues("block_data.reviewer_types"), {
          invitation_subject: getValues("block_data.invitation_subject"),
          invitation_body: getValues("block_data.invitation_body"),
        }),
        { shouldDirty: true }
      );
    }
    setValue("block_data.invitation_mode", mode, { shouldDirty: true });
  };

  const handleSaveDraft = handleSubmit(async (data) => {
    const enabledReviewerTypes = data.block_data.reviewer_types.filter(
      (reviewerType) => reviewerType.is_enabled
    );

    const reviewerTypesPayload = enabledReviewerTypes
      .map((reviewerType) => {
        if (reviewerType.form_template_id == null) {
          return null;
        }

        return {
          id: reviewerType.id,
          title: reviewerType.title,
          type: reviewerType.reviewer_type,
          form_template_id: reviewerType.form_template_id,
          anonymous_threshold:
            isAnonymousSupportedReviewerType(reviewerType.reviewer_type) &&
            reviewerType.is_anonymous
              ? reviewerType.anonymous_threshold
              : 0,
          participants_rule_groups: toWorkflowRuleGroupsPayload(
            reviewerType.participants_rule_groups
          ),
          invitation_subject: reviewerType.invitation_subject,
          invitation_body: reviewerType.invitation_body,
        };
      })
      .filter((reviewerType) => reviewerType != null);

    if (reviewerTypesPayload.length !== enabledReviewerTypes.length) return;

    const reminderSettingsPayload =
      data.reminders_enabled && data.reminder_settings
        ? {
            channels: data.reminder_settings.channels,
            custom_subject: data.reminder_settings.custom_subject,
            custom_body: data.reminder_settings.custom_body,
            mode: data.reminder_settings.mode,
            ...(data.reminder_settings.mode === ReminderMode.custom
              ? {
                  custom_frequency: data.reminder_settings.custom_frequency,
                  custom_period: data.reminder_settings.custom_period,
                }
              : {}),
          }
        : null;

    const blockData: ApiWorkflowBlockActionPerformanceReviewSave = {
      title: data.block_data.title,
      description: data.block_data.description,
      emoji: useEmoji ? data.block_data.emoji : null,
      use_ai: data.block_data.use_ai,
      participants_rule_groups: toWorkflowRuleGroupsPayload(
        data.block_data.participants_rule_groups
      ),
      invitation_subject: data.block_data.invitation_subject,
      invitation_body: data.block_data.invitation_body,
      invitation_mode: data.block_data.invitation_mode,
      timeframe_period_type: data.block_data.timeframe_period_type,
      timeframe_period_value: data.block_data.timeframe_period_value,
      reviewer_types: reviewerTypesPayload,
      reminder_settings: reminderSettingsPayload,
    };

    try {
      await updateBlockAction.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
        payload: {
          title: data.title,
          ...getWorkflowActionTimingPayload(data),
          conditions: conditions.map((condition) => ({ ...condition })),
          block_data: blockData,
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.blockEdited, {
        [analyticProperties.id]: workflowVersion.workflow_id,
        [analyticProperties.actionType]: ApiWorkflowBlockActionTypes.performance_review,
      });

      close(id);
    } catch {
      // handle by hook
    }
  });

  return (
    <Modal
      {...props}
      variant={"side"}
      title={t("performance_review:create_performance_review")}
      layout={"stretch"}
      withCloser
      closeByEscEnabled
      size={"lg"}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            size={"lg"}
            className={"w-full"}
            type={"submit"}
            isLoading={updateBlockAction.isPending}
            onClick={handleSaveDraft}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={handleSaveDraft}>
        <Tabs onActiveTabChange={setActiveTab} activeTab={activeTab}>
          <TabItem title={t("performance_review:performance_review")}>
            <Stack className={"py-6"}>
              <Stack>
                <Title header={t("common:details")} paddingBottom />
                <InputWithVariableInsert
                  label={t("common:title")}
                  type="text"
                  {...register("block_data.title")}
                  required={true}
                  error={errors.block_data?.title?.message}
                  variableOptions={getWorkflowPlaceholderOptions(
                    workflowVersion.trigger_event?.event_type
                  )}
                />

                <TextareaWithVariableInsert
                  label={t("common:description")}
                  {...register("block_data.description")}
                  error={errors.block_data?.description?.message}
                  variableOptions={getWorkflowPlaceholderOptions(
                    workflowVersion.trigger_event?.event_type
                  )}
                />

                <Stack gap="md" className="mt-4">
                  <Switch
                    size={"sm"}
                    checked={!useEmoji}
                    onChange={(value) => {
                      setUseEmoji(!value);
                    }}
                    title={t("performance_review:do_not_use_emoji")}
                  />
                  <Controller
                    control={control}
                    name={"block_data.use_ai"}
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        size={"sm"}
                        checked={value}
                        onChange={onChange}
                        title={t("performance_review:use_ai_title")}
                        description={t("performance_review:use_ai_description")}
                      />
                    )}
                  />
                </Stack>
              </Stack>
              <Title
                header={t("performance_review:who_to_review")}
                size={"xs"}
                paddingTop
                paddingBottom
              />
              <RuleGroupsEditor
                control={control}
                fieldName={"block_data.participants_rule_groups"}
                setValue={setValue}
                place={RuleRegistryPlace.workflowPerformanceReviewParticipants}
                countEmployees={canPreviewWorkflowRuleGroups(
                  watchedParticipantGroups,
                  participantConditionTypes.conditionTypeInfos
                )}
                conditionTypes={participantConditionTypes.conditionTypes}
                variant="list"
              />
              <Stack>
                <Title header={t("common:timeframe")} paddingBottom paddingTop />
                <Stack direction={"horizontal"} gap={"md"}>
                  <Input
                    label={t("common:amount")}
                    type="number"
                    min={0}
                    {...register("block_data.timeframe_period_value", { valueAsNumber: true })}
                    required={true}
                    error={errors.block_data?.timeframe_period_value?.message}
                  />
                  <Box className="grow">
                    <Controller
                      control={control}
                      name={"block_data.timeframe_period_type"}
                      render={({ field: { value, onChange } }) => (
                        <Select
                          label={t("common:period")}
                          emptyTitle={t("common:select")}
                          value={value}
                          options={getWorkflowSurveyPeriodItems()}
                          onChange={(periodType) => {
                            onChange(periodType as ApiWorkflowSurveyPeriodType);
                          }}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Stack>
              <WorkflowPerformanceReviewReviewerTypesSection
                control={control}
                register={register}
                trigger={trigger}
                setValue={setValue}
                errors={errors}
                eventType={workflowVersion.trigger_event?.event_type}
              />
              <Stack>
                <Title header={t("common:invitation")} paddingBottom paddingTop />
                <Stack gap="sm">
                  <Radio
                    value={ApiPerformanceReviewInvitationMode.common}
                    checked={invitationMode === ApiPerformanceReviewInvitationMode.common}
                    onChange={() => setInvitationMode(ApiPerformanceReviewInvitationMode.common)}
                    title={t("performance_review:invitation_mode_common")}
                    caption={t("performance_review:invitation_mode_common_description")}
                  />
                  <Radio
                    value={ApiPerformanceReviewInvitationMode.by_reviewer_type}
                    checked={invitationMode === ApiPerformanceReviewInvitationMode.by_reviewer_type}
                    onChange={() =>
                      setInvitationMode(ApiPerformanceReviewInvitationMode.by_reviewer_type)
                    }
                    title={t("performance_review:invitation_mode_by_reviewer_type")}
                    caption={t("performance_review:invitation_mode_by_reviewer_type_description")}
                  />
                </Stack>
                {invitationMode === ApiPerformanceReviewInvitationMode.common && (
                  <ControlledPerformanceReviewInvitationFields
                    control={control}
                    subjectName="block_data.invitation_subject"
                    bodyName="block_data.invitation_body"
                    subjectError={errors.block_data?.invitation_subject?.message}
                    bodyError={errors.block_data?.invitation_body?.message}
                    variableOptions={getWorkflowPlaceholderOptions(
                      workflowVersion.trigger_event?.event_type
                    )}
                  />
                )}
              </Stack>
              <Stack gap={"md"}>
                <Title header={t("core:notifications_and_reminders")} paddingBottom paddingTop />
                <WorkflowReminderSettingsSection
                  control={control}
                  setValue={setValue}
                  hasSmartBase={hasTimeframe}
                  smartBaseValue={timeframePeriodValue ?? 0}
                  requiredText={t("core:reminder_requires_period")}
                  renderHeader={({ checked, onChange }) => (
                    <Switch
                      size={"sm"}
                      checked={checked}
                      onChange={onChange}
                      title={t("core:turn_on_reminders")}
                      description={t("tasks:overdue_alerts_sent_anyway")}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </TabItem>

          <TabItem title={t("workflow:conditions")}>
            <Stack gap={"lg"} className={"py-6"}>
              <Stack>
                <Title header={t("workflow:block")} size={"xs"} />
                <Input
                  label={t("common:title")}
                  type="text"
                  {...register("title")}
                  error={errors.title?.message}
                />
              </Stack>
              <WorkflowActionTimingEditor
                control={control}
                register={register}
                errors={errors}
                triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
              />

              <WorkflowBlockConditionsList
                conditions={conditions}
                onAdd={handleAddCondition}
                onEdit={handleEditCondition}
                onDelete={handleDeleteCondition}
              />
            </Stack>
          </TabItem>
        </Tabs>
      </form>
    </Modal>
  );
};
